import { useEffect, useState } from 'react';
import { api } from '../api';
import { ApiError, type GmSkinSet } from '../api/types';
import { existe, t } from '../i18n/idioma';
import { nomeDaPecaDoTraje, nomeDoLugarDoTraje } from '../i18n/nomes';

/** A foto do set: o ícone da peça escolhida (o gorro, no de Natal). */
const fotoDoSet = (s: GmSkinSet) => `/sprites/itens/traje/${s.icone}.png`;

/**
 * GM › Skins (techspec-skins §05): os sets de skins, um card por set com a foto de uma peça. Pegar dá o set inteiro
 * ao seu personagem: as peças vão direto para o guarda-roupa. Por enquanto só o GM tem acesso (Renato, 23/10).
 */
export function GmSkins({ ativo }: { ativo: boolean }) {
  const [sets, setSets] = useState<readonly GmSkinSet[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!ativo || sets) return;
    let vivo = true;
    api.gmSkins()
      .then(s => { if (vivo) setSets(s); })
      .catch(err => { if (vivo) setErro(err instanceof ApiError ? err.message : t('comum.recusouPedido')); });
    return () => { vivo = false; };
  }, [ativo, sets]);

  const pegar = async (s: GmSkinSet) => {
    if (busy) return;
    setBusy(s.id);
    setErro(null);
    setMsg(null);
    try {
      const r = await api.gmPegarSkin(s.id);
      setSets(cur => (cur ?? []).map(x => (x.id === r.atual.id ? r.atual : x)));
      setMsg(r.novas.length > 0 ? t('gm.skins.pegou', { n: r.novas.length }) : t('gm.skins.jaTinha'));
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : t('comum.recusouPedido'));
    } finally {
      setBusy(null);
    }
  };

  if (!sets) return <p className={erro ? 'notice' : 'pick__meta'} role={erro ? 'alert' : undefined}>{erro ?? t('comum.carregando')}</p>;

  return (
    <div className="gm-skins">
      <p className="pick__meta">{t('gm.skins.regra')}</p>
      {sets.map(s => {
        const temTudo = s.pecas.every(p => p.tem);
        return (
          <article key={s.id} className="gm-skin">
            <div className="gm-skin__foto"><img src={fotoDoSet(s)} alt="" draggable={false} /></div>
            <div className="gm-skin__txt">
              <b>{existe(`gm.skins.set.${s.id}`) ? t(`gm.skins.set.${s.id}`) : s.nome}</b>
              <ul>
                {s.pecas.map(p => (
                  <li key={p.id} className={p.tem ? 'is-tem' : undefined}>
                    {nomeDaPecaDoTraje(p.id, p.nome)}
                    <small> · {nomeDoLugarDoTraje(p.slot)}</small>
                  </li>
                ))}
              </ul>
              <button type="button" className="btn btn--primary" disabled={!!busy || temTudo} onClick={() => void pegar(s)}>
                {busy === s.id ? t('comum.salvando') : temTudo ? t('gm.skins.temTudo') : t('gm.skins.pegar')}
              </button>
            </div>
          </article>
        );
      })}
      {msg && <p className="pick__meta" role="status">{msg}</p>}
      {erro && <p className="notice" role="alert">{erro}</p>}
    </div>
  );
}
